import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ApiService } from "@/services/api/ApiService";
import { DocumentService } from "@/services/api/DocumentService";
import {
  SaveGenieDocumentInput,
  SaveGenieDocumentResult,
} from "@/types/genie-documents/genie-document";

/**
 * Saves a branded document as a real knowledge-base row.
 *
 * A brand-new document goes live on the Genie Page immediately: the save
 * handler creates the row as a draft, so we flip `is_public` in a follow-up
 * call. Creating a document IS publishing it — unpublishing stays available
 * as the escape hatch (see usePublishGenieDocument). Edits to an existing
 * document never touch `is_public`, so an owner's unpublish sticks.
 */
export const useSaveGenieDocument = () => {
  const queryClient = useQueryClient();

  return useMutation<SaveGenieDocumentResult, Error, SaveGenieDocumentInput>({
    mutationFn: async (input) => {
      const result = await ApiService.invoke<SaveGenieDocumentResult>({
        resource: "genie-documents",
        action: "save",
        data: {
          id: input.id,
          agentId: input.agentId,
          template: input.template,
          title: input.title,
          subtitle: input.subtitle,
          sections: input.sections,
          formatKey: input.formatKey,
          purpose: input.purpose,
          sourceDocumentIds: input.sourceDocumentIds,
        },
      });

      if (!result.created) return result;

      const published = await DocumentService.updateKnowledgeBaseDocument(
        result.document.id,
        { isPublic: true },
      );
      // A failed publish leaves a valid draft — report it, don't fail the save.
      return { ...result, published: Boolean(published) };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      queryClient.invalidateQueries({ queryKey: ["documents-infinite"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-base-docs"] });
      queryClient.invalidateQueries({ queryKey: ["public-help-hub"] });
      queryClient.invalidateQueries({ queryKey: ["public-help-guide"] });
    },
    onError: (error) => {
      toast.error(error.message || "Couldn't save the document");
    },
  });
};
